import React from 'react'
import { useIntl } from 'react-intl'
import { Pressable, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { infoButtonLabel } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { isCTPOForProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  InfoPopoverPlacement,
  InfoPopoverTriggerAction,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { InfoPopover } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/infoPopover/InfoPopover'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { LeapsNwImpContent, LeapsPrdSSContent } from './AssessmentPopovers'
import { useSelectedEpisodeForClient } from '../../lyraTherapy/episodes/data/hooks/useSelectedEpisodeForClient'

type LeapsResultsEntryProps = {
  describeScore: (assessmentName: string, score: number) => string
  onViewAnswersClick: (assessmentName: string) => void
  scores: Scores
  isLastItem?: boolean
}

const TableRow = styled.View<{ theme: ThemeType; alignTop?: boolean; isLastItem?: boolean }>(
  ({ theme: { colors, spacing }, alignTop, isLastItem }) => ({
    flexDirection: 'row',
    alignItems: alignTop ? 'flex-start' : 'center',
    padding: spacing['24px'],
    borderBottomWidth: isLastItem ? '0' : '1px',
    borderBottomColor: isLastItem ? undefined : colors.borderDefault,
  }),
)

const AssessmentContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '320px',
  alignItems: 'center',
})

const ViewAnswersButton = styled(TextButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
  marginTop: theme.spacing['6px'],
}))

const ScoreContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '80px',
})

const QuestionContainer = styled.View<{ theme: ThemeType; columnLayout?: boolean }>(
  ({ theme: { spacing }, columnLayout }) => ({
    flexDirection: columnLayout ? 'column' : 'row',
    alignItems: columnLayout ? 'flex-start' : 'center',
    marginTop: spacing['8px'],
  }),
)

const InfoIconContainer = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['4px'],
  marginTop: theme.spacing['4px'],
  zIndex: 1,
}))

export const LeapsResultsEntry: React.FC<LeapsResultsEntryProps> = ({
  scores,
  onViewAnswersClick,
  describeScore,
  isLastItem,
}) => {
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const isPerformanceCoachingEpisode =
    !!selectedEpisode?.program_taxonomy &&
    isCTPOForProgram(selectedEpisode.program_taxonomy, ProgramNames.PerformanceCoaching)

  if (isPerformanceCoachingEpisode) return null
  if (scores.LEAPS === undefined || scores.LEAPS === null) return null
  return (
    <TableRow testID={tID('ClientOutcomes-results-leaps')} alignTop isLastItem={isLastItem}>
      <AssessmentContentContainer>
        <Title text='LEAPS' size={TitleSize.MEDIUM} />
        <ViewAnswersButton
          testID={tID(`ClientOutcomes-viewAnswers-leaps`)}
          onPress={() => {
            onViewAnswersClick('LEAPS')
          }}
          text='view answers'
        />
      </AssessmentContentContainer>
      <ScoreContentContainer>
        <Title testID={tID(`client-leaps-score`)} text={scores.LEAPS} size={TitleSize.MEDIUM} />
      </ScoreContentContainer>
      <View>
        <Title
          testID={tID(`client-leaps-score-description`)}
          text={describeScore('LEAPS', scores.LEAPS || 0)}
          size={TitleSize.MEDIUM}
        />

        {scores['LEAPS-NW-IMP'] !== undefined && scores['LEAPS-NW-IMP'] !== null && (
          <>
            <QuestionContainer>
              <Title text='Percent Non-work Impairment' size={TitleSize.XSMALL} />
              <InfoPopover
                content={<LeapsNwImpContent />}
                trigger={
                  <InfoIconContainer accessibilityRole='button' accessibilityLabel={formatMessage(infoButtonLabel)}>
                    <InfoIcon size={16} fillColor={theme.colors.iconActive} />
                  </InfoIconContainer>
                }
                placement={InfoPopoverPlacement.TOP}
                popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
                shouldDisplayCloseButton={false}
              />
            </QuestionContainer>
            <BodyText
              text={`${scores['LEAPS-NW-IMP']}%`}
              size={BodyTextSize.DEFAULT}
              color={theme.colors.textSecondary}
            />
          </>
        )}

        {scores['LEAPS-PRD-SS'] !== undefined && scores['LEAPS-PRD-SS'] !== null && (
          <>
            <QuestionContainer>
              <Title text='Productivity Subscale' size={TitleSize.XSMALL} />
              <InfoPopover
                content={<LeapsPrdSSContent />}
                trigger={
                  <InfoIconContainer accessibilityRole='button' accessibilityLabel={formatMessage(infoButtonLabel)}>
                    <InfoIcon size={16} fillColor={theme.colors.iconActive} />
                  </InfoIconContainer>
                }
                placement={InfoPopoverPlacement.TOP}
                popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
                shouldDisplayCloseButton={false}
              />
            </QuestionContainer>
            <BodyText
              text={`${scores['LEAPS-PRD-SS']}`}
              size={BodyTextSize.DEFAULT}
              color={theme.colors.textSecondary}
            />
          </>
        )}
      </View>
    </TableRow>
  )
}
